import * as React from 'react';
import { FaLock } from 'react-icons/fa';
import TooltipComponent from 'components/Tooltip';
import classNames from 'styles/utils/classNames';

export interface SegmentItem {
  value: string;
  label: string;
  // Premium-gated for the current viewer: rendered dimmed with a lock and routed to the
  // upgrade CTA on click instead of toggling.
  locked?: boolean;
}

interface Props {
  label?: string;
  items: SegmentItem[];
  isActive: (value: string) => boolean;
  onSelect: (value: string) => void;
  // Invoked when a `locked` item is clicked (e.g. open the pricing page).
  onLockedSelect?: (value: string) => void;
  ariaLabel?: string;
  // Hover description for the whole section (the labels are dropped in the top nav, so this
  // is how the group explains itself).
  tooltip?: string;
  className?: string;
  // Optional element rendered just after the segment group (e.g. a clear button).
  trailing?: React.ReactNode;
}

/**
 * The one segmented-pill primitive shared by every top-nav flow control (Mode, Sides,
 * Tickers, Presets) so they read as a single system: a joined group of pills, brand-primary
 * fill for the active segment, a consistent lock treatment for premium-gated options, and an
 * optional on-hover section tooltip.
 */
export default function FlowSegmentedControl({
  label,
  items,
  isActive,
  onSelect,
  onLockedSelect,
  ariaLabel,
  tooltip,
  className,
  trailing,
}: Props) {
  const control = (
    <div className={classNames('inline-flex items-center', className)}>
      {label && (
        <span className="mr-1.5 flex-shrink-0 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
          {label}
        </span>
      )}
      <div
        role="group"
        aria-label={ariaLabel || label}
        className="inline-flex items-center overflow-hidden rounded border border-white/10 bg-black/20"
      >
        {items.map((item, index) => {
          const active = isActive(item.value);
          const locked = !!item.locked;

          return (
            <button
              key={item.value}
              type="button"
              aria-label={item.label}
              aria-pressed={active}
              onClick={() => (locked ? onLockedSelect?.(item.value) : onSelect(item.value))}
              className={classNames(
                'flex h-7 items-center gap-1 whitespace-nowrap px-2 text-[11px] font-bold leading-none transition-colors',
                index > 0 && 'border-l border-white/10',
                active && !locked && 'bg-brand-primary text-white',
                !active && !locked && 'text-gray-400 hover:bg-white/5 hover:text-white',
                locked && 'cursor-pointer text-gray-600 hover:text-yellow-400',
              )}
            >
              {item.label}
              {locked && <FaLock className="h-2.5 w-2.5 text-yellow-400/80" />}
            </button>
          );
        })}
        {trailing}
      </div>
    </div>
  );

  if (tooltip) {
    return (
      <TooltipComponent title={tooltip} position="bottom" variant="dark">
        {control}
      </TooltipComponent>
    );
  }

  return control;
}
